import React, { useState } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
} from '@mui/material';
import classNames from 'classnames';
import { useMediaQuery } from 'react-responsive';
import { SVGS } from '../../../../assets/images';
import styles from '../../../../components/shared/delete-dialog/delete-dialog.module.scss';
import actionStyles from '../../../../components/shared/action-cancel-buttons-area/action-cancel-buttons-area.module.scss';

interface Props {
    open: boolean;
    title: string;
    message: string;
    children?: React.ReactNode;
    actionLabel: string;
    cancelLabel?: string;
    secondaryActionLabel?: string;
    icon?: React.ReactNode;
    onCancel: () => void;
    onConfirm: () => Promise<void> | void;
    onSecondaryConfirm?: () => Promise<void> | void;
}

export default function ActionConfirmDialog({
    open,
    title,
    message,
    children,
    actionLabel,
    cancelLabel = 'Cancel',
    secondaryActionLabel,
    icon,
    onCancel,
    onConfirm,
    onSecondaryConfirm,
}: Props) {
    const isMobile = useMediaQuery({ maxWidth: 1000 });
    const [submitting, setSubmitting] = useState(false);

    const handleConfirm = async () => {
        setSubmitting(true);
        try {
            await onConfirm();
        } finally {
            setSubmitting(false);
        }
    };

    const handleSecondaryConfirm = async () => {
        if (!onSecondaryConfirm) {
            return;
        }

        setSubmitting(true);
        try {
            await onSecondaryConfirm();
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog
            open={open}
            fullScreen={isMobile ? true : false}
            onClose={submitting ? undefined : onCancel}
            PaperProps={{ className: styles.dialogPaper }}
        >
            {isMobile ? null : (
                <IconButton
                    aria-label="close"
                    onClick={onCancel}
                    disabled={submitting}
                    className={styles.dialogCloseButton}
                >
                    <SVGS.CrossIcon style={{ width: '13.185px', height: '13.185px' }} />
                </IconButton>
            )}
            <DialogContent className={styles.dialogContentRoot}>
                <DialogContent className={styles.dialogContentInner}>
                    <div className={styles.dialogDeleteHeader}>
                        <div className={styles.dialogDeleteIcon}>
                            {icon ?? <SVGS.ActionIcon style={{ width: '32px', height: '32px', fill: 'var(--default-svg-color)' }} />}
                        </div>
                        {title}
                    </div>
                    {message}
                    {children}
                </DialogContent>
            </DialogContent>
            <DialogActions className={styles.dialogActions}>
                {isMobile ? (
                    <>
                        <button
                            onClick={() => { void handleConfirm(); }}
                            className={actionStyles.buttonActionCancelArea}
                            disabled={submitting}
                            autoFocus
                        >
                            {actionLabel}
                        </button>
                        {secondaryActionLabel && onSecondaryConfirm ? (
                            <button
                                onClick={() => { void handleSecondaryConfirm(); }}
                                className={actionStyles.buttonActionCancelArea}
                                disabled={submitting}
                            >
                                {secondaryActionLabel}
                            </button>
                        ) : null}
                        <button
                            onClick={onCancel}
                            className={classNames(actionStyles.buttonActionCancelArea, actionStyles.cancel)}
                            disabled={submitting}
                        >
                            {cancelLabel}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            onClick={onCancel}
                            className={classNames(actionStyles.buttonActionCancelArea, actionStyles.cancel)}
                            disabled={submitting}
                        >
                            {cancelLabel}
                        </button>
                        {secondaryActionLabel && onSecondaryConfirm ? (
                            <button
                                onClick={() => { void handleSecondaryConfirm(); }}
                                className={actionStyles.buttonActionCancelArea}
                                disabled={submitting}
                            >
                                {secondaryActionLabel}
                            </button>
                        ) : null}
                        <button
                            onClick={() => { void handleConfirm(); }}
                            className={actionStyles.buttonActionCancelArea}
                            disabled={submitting}
                            autoFocus
                        >
                            {actionLabel}
                        </button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
